import { useEffect, useState } from 'react'

/**
 * A mermaid diagram, drawn on the device. The library is bundled but loaded on
 * first use (a dynamic import), never in the main bundle. `securityLevel:
 * 'strict'` makes mermaid sanitise what it returns, which is the one SVG this
 * app puts into the page as markup. Colours are the `--diagram-*` variables in
 * index.css (themed per appearance), read when the diagram is drawn. A source
 * that does not parse shows itself with a one-line reason, never a blank.
 */

const VARS = ['text', 'muted', 'node', 'surface', 'edge', 'accent'] as const
type Palette = Record<(typeof VARS)[number], string>

const readPalette = (): Palette => {
  const cs = getComputedStyle(document.documentElement)
  const get = (n: string) => cs.getPropertyValue(`--diagram-${n}`).trim()
  return Object.fromEntries(VARS.map((v) => [v, get(v)])) as Palette
}

let seq = 0

type Drawn = { src: string; theme: string; svg?: string; error?: string }

const oneLine = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e)
  const line = raw.split('\n').find((l) => l.trim()) ?? 'Could not draw this diagram'
  return line.length > 140 ? `${line.slice(0, 137)}…` : line
}

const draw = async (source: string, p: Palette): Promise<string> => {
  const mermaid = (await import('mermaid')).default
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'strict',
    suppressErrorRendering: true,
    theme: 'base',
    fontFamily: 'inherit',
    // drawn at its own size and scrolled sideways when wider than the column: shrunk to fit,
    // a long LR flowchart became a strip with labels a few pixels tall (the UI audit, 10 Oct)
    flowchart: { htmlLabels: false, useMaxWidth: false },
    sequence: { useMaxWidth: false },
    state: { useMaxWidth: false },
    class: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false },
    journey: { useMaxWidth: false },
    themeVariables: {
      background: p.surface,
      primaryColor: p.node,
      primaryTextColor: p.text,
      primaryBorderColor: p.edge,
      secondaryColor: p.surface,
      tertiaryColor: p.surface,
      lineColor: p.edge,
      textColor: p.text,
      noteBkgColor: p.surface,
      noteTextColor: p.text,
      noteBorderColor: p.edge,
      mainBkg: p.node,
      nodeBorder: p.edge,
      clusterBkg: p.surface,
      clusterBorder: p.edge,
      edgeLabelBackground: p.surface,
      titleColor: p.text,
      actorBkg: p.node,
      actorBorder: p.edge,
      actorTextColor: p.text,
      actorLineColor: p.muted,
      signalColor: p.text,
      signalTextColor: p.text,
      labelBoxBkgColor: p.node,
      labelBoxBorderColor: p.edge,
      labelTextColor: p.text,
      loopTextColor: p.text,
      activationBkgColor: p.surface,
      activationBorderColor: p.accent,
    },
  })
  await mermaid.parse(source)
  const id = `mmd-${++seq}`
  const { svg } = await mermaid.render(id, source)
  return svg
}

export const MermaidDiagram = ({ source, className }: { source: string; className?: string }) => {
  const [theme, setTheme] = useState('')
  const [drawn, setDrawn] = useState<Drawn | null>(null)
  const src = source.trim()

  // follow the appearance: re-read the palette whenever the root's tokens change
  useEffect(() => {
    const read = () => setTheme(JSON.stringify(readPalette()))
    read()
    const obs = new MutationObserver(read)
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'data-appearance', 'class'],
    })
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (!theme || !src) return
    let live = true
    // a short pause so typing in the editor does not draw every keystroke
    const t = window.setTimeout(() => {
      draw(src, JSON.parse(theme) as Palette).then(
        (svg) => live && setDrawn({ src, theme, svg }),
        (e: unknown) => live && setDrawn({ src, theme, error: oneLine(e) }),
      )
    }, 250)
    return () => {
      live = false
      window.clearTimeout(t)
    }
  }, [src, theme])

  if (!src) return null
  const mine = drawn && drawn.src === src ? drawn : null
  if (mine?.error)
    return (
      <div className={className} data-testid="diagram-error">
        <p className="text-xs text-muted mb-1">Could not draw this diagram: {mine.error}</p>
        <pre className="font-mono text-[12.5px] leading-snug whitespace-pre-wrap break-words rounded-[var(--radius-card-inner)] bg-raised px-3 py-2 overflow-x-auto">
          {src}
        </pre>
      </div>
    )
  if (!mine?.svg)
    // while it draws (a moment, once the library is in) the source is already there to read
    return (
      <pre
        className={`${className ?? ''} font-mono text-[12.5px] leading-snug whitespace-pre-wrap break-words rounded-[var(--radius-card-inner)] bg-raised px-3 py-2 overflow-x-auto opacity-60`}
      >
        {src}
      </pre>
    )
  return (
    <div
      className={`${className ?? ''} mermaid-diagram overflow-x-auto`}
      data-testid="diagram"
      // mermaid's own sanitised output (securityLevel 'strict')
      dangerouslySetInnerHTML={{ __html: mine.svg }}
    />
  )
}
